<template>
  <main class="patient-completion-page">
    <section class="patient-completion-shell">
      <aside class="patient-completion-hero">
        <div class="patient-completion-brand">
          <img src="@/assets/images/newdental-logo.svg" alt="NewDental" />
          <strong>NewDental</strong>
        </div>
        <div>
          <span>Cadastro do paciente</span>
          <h1>Complete seus dados para agilizar o atendimento.</h1>
          <p>
            Suas informações serão enviadas diretamente para a clínica e usadas apenas para
            atualizar seu prontuário cadastral.
          </p>
        </div>
      </aside>

      <section class="patient-completion-card">
        <div v-if="loading" class="patient-completion-state">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <span>Carregando cadastro...</span>
        </div>

        <div v-else-if="finished" class="patient-completion-state patient-completion-state--success">
          <v-icon color="success" size="48">mdi-check-circle-outline</v-icon>
          <h2>Cadastro atualizado</h2>
          <p>Obrigado. A clínica já recebeu seus dados atualizados.</p>
        </div>

        <div v-else-if="error" class="patient-completion-state">
          <v-icon color="error" size="48">mdi-alert-circle-outline</v-icon>
          <h2>Link indisponível</h2>
          <p>{{ error }}</p>
        </div>

        <v-form v-else ref="form" class="patient-completion-form" @submit.prevent="submit">
          <div class="patient-completion-heading">
            <small>{{ patient.company && patient.company.name ? patient.company.name : "NewDental" }}</small>
            <h2>Finalizar cadastro</h2>
            <p>Revise e complete seus dados principais.</p>
          </div>

          <div class="patient-completion-grid">
            <v-text-field
              label="Nome completo"
              v-model="form.name"
              outlined
              dense
              hide-details="auto"
              class="span-2"
              :rules="[$rules.required]"
            ></v-text-field>
            <v-text-field
              label="CPF"
              v-model="form.registration"
              v-mask="'###.###.###-##'"
              outlined
              dense
              hide-details="auto"
            ></v-text-field>
            <v-text-field
              label="Data de nascimento"
              v-model="form.birthday"
              type="date"
              outlined
              dense
              hide-details="auto"
            ></v-text-field>
            <v-select
              label="Gênero"
              :items="genderTypes"
              item-value="id"
              item-text="name"
              v-model="form.gender"
              outlined
              dense
              hide-details="auto"
            ></v-select>
            <v-text-field
              label="E-mail"
              v-model="form.email"
              outlined
              dense
              hide-details="auto"
            ></v-text-field>
            <v-text-field
              label="Telefone"
              v-model="form.phone.value"
              v-mask="['(##) ####-####', '(##) #####-####']"
              outlined
              dense
              hide-details="auto"
            ></v-text-field>
          </div>

          <div class="patient-completion-section">
            <h3>Endereço</h3>
            <div class="patient-completion-grid">
              <v-text-field
                label="CEP"
                v-model="form.address.zipCode"
                v-mask="'#####-###'"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
              <v-text-field label="Estado" v-model="form.address.state" outlined dense hide-details="auto"></v-text-field>
              <v-text-field label="Cidade" v-model="form.address.city" outlined dense hide-details="auto"></v-text-field>
              <v-text-field label="Bairro" v-model="form.address.district" outlined dense hide-details="auto"></v-text-field>
              <v-text-field label="Rua" v-model="form.address.street" outlined dense hide-details="auto" class="span-2"></v-text-field>
              <v-text-field label="Número" v-model="form.address.number" outlined dense hide-details="auto"></v-text-field>
              <v-text-field label="Complemento" v-model="form.address.complement" outlined dense hide-details="auto"></v-text-field>
            </div>
          </div>

          <v-btn block large color="primary" elevation="0" type="submit" :loading="saving">
            Enviar cadastro
          </v-btn>
        </v-form>
      </section>
    </section>
  </main>
</template>

<script>
import axios from "axios";
import genderTypes from "@/common/data/gender-types.data";

export default {
  name: "PatientCompletion",

  data: () => ({
    loading: true,
    saving: false,
    finished: false,
    error: "",
    patient: {},
    genderTypes,
    form: {
      name: "",
      registration: "",
      email: "",
      gender: null,
      birthday: "",
      phone: {
        type: 3,
        value: "",
      },
      address: {
        zipCode: "",
        country: "BR",
        state: "",
        city: "",
        district: "",
        street: "",
        number: "",
        complement: "",
      },
    },
  }),

  async created() {
    await this.load();
  },

  methods: {
    async load() {
      this.loading = true;
      this.error = "";

      try {
        const response = await axios.get(`/patients/completion/${this.$route.params.token}`);
        this.patient = response.data.data || {};
        this.fillForm(this.patient);
      } catch (error) {
        this.error = this.$codes(error) || "O link informado expirou ou não está mais disponível.";
      } finally {
        this.loading = false;
      }
    },

    fillForm(patient) {
      this.form.name = patient.name || "";
      this.form.registration = patient.registration || "";
      this.form.email = patient.email || "";
      this.form.gender = patient.gender || null;
      this.form.birthday = patient.birthday ? String(patient.birthday).split("T")[0] : "";
      this.form.phone = {
        type: patient.phone && patient.phone.type ? patient.phone.type : 3,
        value: patient.phone && patient.phone.value ? patient.phone.value : "",
      };
      this.form.address = {
        ...this.form.address,
        ...(patient.address || {}),
      };
    },

    buildPayload() {
      const phoneValue = (this.form.phone.value || "").replace(/\D/g, "");

      return {
        name: this.form.name,
        registration: (this.form.registration || "").replace(/\D/g, ""),
        email: this.form.email || null,
        gender: this.form.gender || null,
        birthday: this.form.birthday || null,
        phone: phoneValue ? { type: 3, value: phoneValue } : null,
        address: {
          zipCode: (this.form.address.zipCode || "").replace(/\D/g, ""),
          country: this.form.address.country || "BR",
          state: this.form.address.state || null,
          city: this.form.address.city || null,
          district: this.form.address.district || null,
          street: this.form.address.street || null,
          number: this.form.address.number || null,
          complement: this.form.address.complement || null,
        },
      };
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      this.saving = true;
      try {
        await axios.patch(`/patients/completion/${this.$route.params.token}`, this.buildPayload());
        this.finished = true;
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), {
          position: "top-right",
          timeout: 5000,
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.patient-completion-page {
  background: #f4f5f3;
  min-height: 100vh;
  padding: 32px;
}

.patient-completion-shell {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 22px 80px rgba(28, 36, 34, 0.12);
  display: grid;
  grid-template-columns: minmax(320px, 0.8fr) minmax(420px, 1fr);
  min-height: calc(100vh - 64px);
  overflow: hidden;
}

.patient-completion-hero {
  background: linear-gradient(135deg, #8f43d3 0%, #4f1f87 100%);
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 42px;

  span {
    font-size: 13px;
    font-weight: 800;
    text-transform: uppercase;
  }

  h1 {
    font-size: 42px;
    line-height: 1.05;
    margin: 18px 0;
    max-width: 560px;
  }

  p {
    color: rgba(255, 255, 255, 0.8);
    font-size: 18px;
    max-width: 520px;
  }
}

.patient-completion-brand {
  align-items: center;
  display: flex;
  font-size: 20px;
  gap: 10px;

  img {
    height: 34px;
    width: 34px;
  }
}

.patient-completion-card {
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 48px;
}

.patient-completion-form {
  max-width: 760px;
  width: 100%;
}

.patient-completion-heading {
  margin-bottom: 28px;

  small {
    color: var(--v-primary-base);
    font-weight: 800;
    text-transform: uppercase;
  }

  h2 {
    color: #101c1a;
    font-size: 32px;
    line-height: 1.1;
    margin: 8px 0;
  }

  p {
    color: #74807a;
    font-size: 16px;
    margin: 0;
  }
}

.patient-completion-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.span-2 {
  grid-column: span 2;
}

.patient-completion-section {
  border-top: 1px solid #eef0f2;
  margin: 28px 0;
  padding-top: 24px;

  h3 {
    color: #101c1a;
    font-size: 18px;
    margin: 0 0 16px;
  }
}

.patient-completion-state {
  align-items: center;
  color: #6b7470;
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 420px;
  text-align: center;

  h2 {
    color: #101c1a;
    font-size: 28px;
    margin: 0;
  }
}

@media (max-width: 900px) {
  .patient-completion-page {
    padding: 0;
  }

  .patient-completion-shell {
    border-radius: 0;
    grid-template-columns: 1fr;
  }

  .patient-completion-hero {
    min-height: 260px;
  }

  .patient-completion-card {
    padding: 28px 18px;
  }

  .patient-completion-grid {
    grid-template-columns: 1fr;
  }

  .span-2 {
    grid-column: span 1;
  }
}
</style>
